import React, { useState } from 'react';
import { auth } from '../../firebase';
import {
  signInWithEmailAndPassword,
  signInAnonymously,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import { getDoc, doc, getFirestore, setDoc, serverTimestamp } from 'firebase/firestore';
import { firestore } from '../../firebase';
import { sanitize } from '../../utils/sanitize';
import { upsertLinkedAccountWithPassword, ensureSelfLinkedAccount } from '../../utils/accountLinkStorage';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { BrandMark } from '../shared/BrandLogo';

// Canonisation SÛRE : n’enlève les points et alias + que pour Gmail (LOGIN UNIQUEMENT)
function safeCanonicalizeEmail(input) {
  if (!input) return '';
  const trimmed = input.trim();

  const atIdx = trimmed.lastIndexOf('@');
  if (atIdx === -1) return trimmed;

  const local = trimmed.slice(0, atIdx);
  const domain = trimmed.slice(atIdx + 1).toLowerCase();

  const isGmail = domain === 'gmail.com' || domain === 'googlemail.com';
  const normalizedDomain = isGmail ? 'gmail.com' : domain;

  if (isGmail) {
    const localLower = local.toLowerCase();
    const plusIdx = localLower.indexOf('+');
    const withoutPlus = plusIdx === -1 ? localLower : localLower.slice(0, plusIdx);
    const withoutDots = withoutPlus.replace(/\./g, '');
    return `${withoutDots}@${normalizedDomain}`;
  }
  return `${local}@${normalizedDomain}`;
}

const LoginGeneral = () => {
  const location = useLocation();
  const params   = new URLSearchParams(location.search);
  const rawEmail = params.get('email') || '';
  const initialEmail = sanitize(rawEmail);

  const [email, setEmail]               = useState(initialEmail);
  const [password, setPassword]         = useState('');
  const [message, setMessage]           = useState(null);
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [isGuestLoading, setIsGuestLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();
  const { code } = useParams();



  const routeByRole = async (role, user, userData) => {
    localStorage.setItem('userEmail', user.email || '');
    localStorage.setItem('userRole', role);

    if (role === 'client') {
      navigate('/clientdocs');

    } else if (role === 'commercant') {
      if (userData.keystatus === 'active') {
        navigate('/merchant-dashboard');
      } else {
        await setDoc(
          doc(firestore, 'users', user.uid),
          { ...userData, keystatus: 'active' },
          { merge: true }
        );
        navigate('/merchant-dashboard');
      }

    } else if (role === 'commercantpending') {
      navigate('/merchantprocesspaiement', { state: { code, uid: user.uid } });

    } else if (role === 'affiliatepending') {
      // ⬅️ nouvelle redirection « salon d’attente »
      navigate('/affiliatespending', { state: { uid: user.uid } });

    } else if (role === 'affiliates' || role === 'AffiliateRegie') {
      navigate('/affiliatesdashboard');

    } else if (role === 'admin') {
      navigate('/admin-dashboard');

    } else if (role === 'cashier') {
      navigate('/cashierdashboard');

    } else {
      setMessage({ text: 'Rôle inconnu.', isSuccess: false });
    }
  };

  const rememberLinkedAccount = (user, pwd, userData, emailHint) => {
    if (!user?.uid || !pwd) return;
    const emailToStore = user.email || emailHint || '';
    if (!emailToStore) return;
    try {
      const res = upsertLinkedAccountWithPassword({
        ownerUid: user.uid,
        uid: user.uid,
        email: emailToStore,
        password: pwd,
        pseudo: userData?.pseudo,
        role: userData?.role,
        lastUsedAt: Date.now(),
      });
      if (!res.ok && res.error === 'limit') {
        console.warn('[LoginGeneral] Limite de comptes associés atteinte, impossible de stocker ce compte.');
      }
    } catch (err) {
      console.error('[LoginGeneral] Impossible de mémoriser le compte courant:', err);
    }
  };

  const doSignIn = async (loginEmail, pwd) => {
    const cred = await signInWithEmailAndPassword(auth, loginEmail, pwd);
    const user = cred.user;
    const snap = await getDoc(doc(firestore, 'users', user.uid));

    if (!snap.exists()) {
      throw new Error('NO_ROLE_DOC');
    }
    const data = snap.data();
    rememberLinkedAccount(user, pwd, data, loginEmail);
    try {
      await ensureSelfLinkedAccount({
        uid: user.uid,
        email: user.email || loginEmail,
        password: pwd,
        pseudo: data?.pseudo,
        role: data?.role,
      });
    } catch (err) {
      console.error('[LoginGeneral] ensureSelfLinkedAccount error:', err);
    }
    await routeByRole(data.role, user, data);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoginLoading(true);
    setMessage(null);

    const sanitizedEmail = sanitize(email);
    const canonicalEmail = safeCanonicalizeEmail(sanitizedEmail);
    const rawPassword = password;

    // Détermine les variantes à essayer (évite un double appel inutile si identiques)
    const emailsToTry = canonicalEmail !== sanitizedEmail
      ? [sanitizedEmail, canonicalEmail]
      : [sanitizedEmail];

    const parseMsg = (code, message) => {
      if (code === 'auth/invalid-email') return 'Email invalide.';
      if (code === 'auth/user-disabled') return 'Compte désactivé.';
      if (code === 'auth/user-not-found') return 'Compte introuvable.';
      if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') return 'Email ou mot de passe incorrect.';
      if (message === 'NO_ROLE_DOC') return 'Aucun rôle trouvé pour cet utilisateur.';
      return 'Erreur lors de la connexion. Vérifiez vos identifiants.';
    };

    try {
      let lastErr = null;
      for (const tryEmail of emailsToTry) {
        try {
          await doSignIn(tryEmail, rawPassword);
          return; // succès
        } catch (err) {
          lastErr = err;
          const c = err?.code || '';
          // Si l'erreur n'est pas liée aux credentials, inutile d'essayer la variante suivante
          if (c !== 'auth/user-not-found' && c !== 'auth/invalid-credential' && c !== 'auth/invalid-email') {
            break;
          }
        }
      }

      if (lastErr) {
        console.error('Login error:', lastErr?.code, lastErr?.message);
        setMessage({ text: parseMsg(lastErr?.code || '', lastErr?.message || ''), isSuccess: false });
      }
    } finally {
      setIsLoginLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    const input = (email || '').trim();
    if (!input) {
      setMessage({ text: 'Veuillez entrer votre adresse email.', isSuccess: false });
      setTimeout(() => setMessage(null), 4000);
      return;
    }

    const at = input.lastIndexOf('@');
    const local = at === -1 ? input : input.slice(0, at);
    const domain = at === -1 ? '' : input.slice(at + 1).toLowerCase();
    const normalizedDomain = (domain === 'googlemail.com') ? 'gmail.com' : domain;
    const target = at === -1 ? input : `${local}@${normalizedDomain}`;

    try {
      await sendPasswordResetEmail(auth, target);
      setMessage({ text: 'Si un compte existe pour cet email, un lien a été envoyé. ✅', isSuccess: true });
    } catch (err) {
      console.error('Password reset error:', err?.code, err?.message);
      setMessage({ text: 'Si un compte existe pour cet email, un lien a été envoyé. ✅', isSuccess: true });
    } finally {
      setTimeout(() => setMessage(null), 4000);
    }
  };

  const handleGuestSignup = async () => {
    setIsGuestLoading(true);
    setMessage(null);
    try {
      const userCredential = await signInAnonymously(auth);
      const user = userCredential.user;
      const db   = getFirestore();
      await setDoc(doc(db, "users", user.uid), {
        uid:       user.uid,
        role:      'client',
        createdAt: serverTimestamp()
      });
      localStorage.setItem('userRole', 'client');
      setMessage({ text: "Inscription en mode invité réussie ! ✅", isSuccess: true });
      setTimeout(() => navigate('/clientdocs'), 2000);
    } catch {
      setMessage({
        text: "Erreur lors de l'inscription en mode invité. Veuillez réessayer.",
        isSuccess: false
      });
      setTimeout(() => setMessage(null), 4000);
    } finally {
      setIsGuestLoading(false);
    }
  };

  const isAnyLoading = isLoginLoading || isGuestLoading;

  return (
    <div className="eb-auth">
      <div className="eb-auth__card">
        <div className="eb-auth__brand"><BrandMark /></div>
        <h1 className="eb-auth__title">Connexion</h1>
        <p className="eb-auth__subtitle">Accédez à votre espace Ecobill</p>

        <form className="eb-auth__form" onSubmit={handleLogin}>
          <div className="eb-field">
            <label className="eb-label" htmlFor="login-email">Adresse e-mail</label>
            <input
              id="login-email"
              className="eb-input"
              type="email"
              autoComplete="email"
              inputMode="email"
              placeholder="vous@exemple.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isAnyLoading}
            />
          </div>

          <div className="eb-field">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <label className="eb-label" htmlFor="login-password">Mot de passe</label>
              <button
                type="button"
                className="eb-link"
                style={{ fontSize: 13 }}
                onClick={handlePasswordReset}
                disabled={isAnyLoading}
              >
                Mot de passe oublié ?
              </button>
            </div>
            <div style={{ position: 'relative' }}>
              <input
                id="login-password"
                className="eb-input"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isAnyLoading}
                style={{ paddingRight: 46 }}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                style={{
                  position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                  width: 36, height: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  background: 'none', border: 0, borderRadius: 8, color: 'var(--eb-muted)', cursor: 'pointer',
                }}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          {message && (
            <div className={`eb-alert ${message.isSuccess ? 'eb-alert--success' : 'eb-alert--danger'}`} role="alert">
              {message.text}
            </div>
          )}

          <button type="submit" className="eb-btn eb-btn--primary eb-btn--lg eb-btn--block" disabled={isAnyLoading}>
            {isLoginLoading ? <span className="eb-spinner eb-spinner--sm eb-spinner--light" aria-hidden="true" /> : null}
            Se connecter
          </button>

          <div className="eb-auth__sep">ou</div>

          <button
            type="button"
            className="eb-btn eb-btn--secondary eb-btn--lg eb-btn--block"
            onClick={handleGuestSignup}
            disabled={isAnyLoading}
          >
            {isGuestLoading ? <span className="eb-spinner eb-spinner--sm" aria-hidden="true" /> : null}
            Continuer en mode invité
          </button>
        </form>

        <div className="eb-auth__footer">
          Pas encore de compte ? <Link to="/signup">Créer un compte</Link>
        </div>
      </div>
    </div>
  );
};

export default LoginGeneral;
